import React, {forwardRef, useImperativeHandle, useState} from 'react';
import {Button, Modal} from "react-bootstrap";
import Loading from "../../components/loading";
import HelperService from "../../services/helper-service";
import Section from "../../components/section";
import {useBuildingStatService} from "../../services/building-stat-service";
import BuildingStatWidgetContent from "../../components/buildings/building-stat-widget-content";
import DataTableBase from "../../components/datatable/data-table-base";
import FormatService from "../../services/format-service";
import {BuildingStatTypes} from "../../utils/constants";
import MyHighchart from "../../components/my-highchart";

const BuildingStatDetailModal = forwardRef( ({onSuccess=null, ...props}, ref) => {
  const [show, setShow] = useState(false);
  const [model, setModel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [maxYValue, setMaxYValue] = useState('auto');
  const [chartData, setChartData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [capacityKeys, setCapacityKeys] = useState([
    BuildingStatTypes.capacity?.description,
    BuildingStatTypes.capacityAccepted?.description,
    BuildingStatTypes.capacityReserved?.description,
    BuildingStatTypes.capacityFree?.description,
  ]);

  let buildingStatService = useBuildingStatService();

  //expose api for interacting with component
  useImperativeHandle(ref, () => ({
    openModal: openModal,
    closeModal: closeModal,
  }));

  const openModal = (buildingId=null, buildingStatTypeId=null) => {
    setLoading(true);

    let promises = [
     Promise.resolve('initOk'),
      buildingStatService.getStatDetail(buildingId, buildingStatTypeId)
    ];

    Promise.all(promises).then(([init=null, _model=null])=>{

      const newModel = _model;
      const dayjs = HelperService.getDayjs();

      //standard simple types
      if (newModel.chart?.length && newModel.type_sync_id!==BuildingStatTypes.capacity.val.toString()){
        newModel.chart = newModel.chart.map((item)=>{
          item.parsedDate = dayjs(item.x, "DD.MM.YYYY");
          item.x = HelperService.formatDate(item.parsedDate);
          item.y = item.value;
          return item;
        });

        setChartData([...newModel.chart.reverse()]);

        setColumns([{
          name: 'Obdobie',
          selector: row => row.x,
          sortFunction: (a,b)=>HelperService.dateSortFunction(a,b,'parsedDate'),
          sortable: true,
        },{
          name: 'Hodnota',
          selector: row => FormatService.formatNumber(row.value),
          sortable: true,
        }]);

        let newMaxYValue = HelperService.getObjByMaxValueKey(newModel.chart, 'value');
        setMaxYValue(newMaxYValue ? (newMaxYValue.value*1 + newMaxYValue.value*0.1) : 'auto');
      }

      //specific grouped type overrides
      let newCapacityData = {};
      if (newModel.chart?.length && newModel.type_sync_id===BuildingStatTypes.capacity.val.toString()){

        setColumns([{
          name: 'Obdobie',
          selector: row => row.period,
          sortFunction: (a,b)=>HelperService.dateSortFunction(a,b,'parsedDate'),
          sortable: true,
        },{
          name: capacityKeys[0],
          selector: row => FormatService.formatNumber(row[capacityKeys[0]]) + '%',
          sortable: true,
        },{
          name: capacityKeys[1],
          selector: row => FormatService.formatNumber(row[capacityKeys[1]]) + '%',
          sortable: true,
        },{
          name: capacityKeys[2],
          selector: row => FormatService.formatNumber(row[capacityKeys[2]]) + '%',
          sortable: true,
        },{
          name: capacityKeys[3],
          selector: row => FormatService.formatNumber(row[capacityKeys[3]]) + '%',
          sortable: true,
        },{
          name: 'Celkom',
          selector: row => FormatService.formatNumber(row.value) + '%',
          sortable: true,
        }]);

        //prejdeme vsetky statsy, nasledne vsetky dependent statsy. ulozime do mapy aby sme mali unikatne datumy.
        newModel.chart.map((row)=>{
          let parsedDate = dayjs(row.x, "DD.MM.YYYY");
          let formattedDate = HelperService.formatDate(parsedDate);
          let item = newCapacityData.hasOwnProperty(row.x) ? newCapacityData[row.x] : {};
          newCapacityData[row.x] = Object.assign({}, item, {
            period: formattedDate,
            parsedDate: parsedDate,
            x: row.x,
            [capacityKeys[0]]: row.value,
          });
          newCapacityData[row.x].value = (newCapacityData[row.x].hasOwnProperty(capacityKeys[0]) ? newCapacityData[row.x][capacityKeys[0]] : 0)*1
            + (newCapacityData[row.x].hasOwnProperty(capacityKeys[1]) ? newCapacityData[row.x][capacityKeys[1]] : 0)*1
            + (newCapacityData[row.x].hasOwnProperty(capacityKeys[2]) ? newCapacityData[row.x][capacityKeys[2]] : 0)*1;
        });
        newModel.dependencies[0].chart.map((row)=>{
          let parsedDate = dayjs(row.x, "DD.MM.YYYY");
          let formattedDate = HelperService.formatDate(parsedDate);
          let item = newCapacityData.hasOwnProperty(row.x) ? newCapacityData[row.x] : {};
          newCapacityData[row.x] = Object.assign({}, item, {
            period: formattedDate,
            parsedDate: parsedDate,
            x: row.x,
            [capacityKeys[1]]: row.value,
          });
          newCapacityData[row.x].value = (newCapacityData[row.x].hasOwnProperty(capacityKeys[0]) ? newCapacityData[row.x][capacityKeys[0]] : 0)*1
            + (newCapacityData[row.x].hasOwnProperty(capacityKeys[1]) ? newCapacityData[row.x][capacityKeys[1]] : 0)*1
            + (newCapacityData[row.x].hasOwnProperty(capacityKeys[2]) ? newCapacityData[row.x][capacityKeys[2]] : 0)*1;
        });
        newModel.dependencies[1].chart.map((row)=>{
          let parsedDate = dayjs(row.x, "DD.MM.YYYY");
          let formattedDate = HelperService.formatDate(parsedDate);
          let item = newCapacityData.hasOwnProperty(row.x) ? newCapacityData[row.x] : {};
          newCapacityData[row.x] = Object.assign({}, item, {
            period: formattedDate,
            parsedDate: parsedDate,
            x: row.x,
            [capacityKeys[2]]: row.value,
          });
          newCapacityData[row.x].value = (newCapacityData[row.x].hasOwnProperty(capacityKeys[0]) ? newCapacityData[row.x][capacityKeys[0]] : 0)*1
            + (newCapacityData[row.x].hasOwnProperty(capacityKeys[1]) ? newCapacityData[row.x][capacityKeys[1]] : 0)*1
            + (newCapacityData[row.x].hasOwnProperty(capacityKeys[2]) ? newCapacityData[row.x][capacityKeys[2]] : 0)*1;
        });
        newModel.dependencies[2].chart.map((row)=>{
          let parsedDate = dayjs(row.x, "DD.MM.YYYY");
          let formattedDate = HelperService.formatDate(parsedDate);
          let item = newCapacityData.hasOwnProperty(row.x) ? newCapacityData[row.x] : {};
          newCapacityData[row.x] = Object.assign({}, item, {
            period: formattedDate,
            parsedDate: parsedDate,
            x: row.x,
            [capacityKeys[3]]: row.value,
          });
          //tu neprepocitavam celkovo, lebo sa to nezaraduje do sumy.
        });

        //pretransformujeme mapu na spojene data, ktore pouzije tabulka aj graf.
        setChartData(Object.values(newCapacityData));
      }


      setModel(newModel);
      setLoading(false);
    });

    setShow(true);
  }
  const closeModal = () => setShow(false);

  return (
    <>
      <Modal size="xl" show={!!show} onHide={closeModal} centered>
        <Modal.Header closeButton>
          { model && <Modal.Title>Podrobnosti pre {!model?.building_stat_type?.title ? 'údaj' : model.building_stat_type.title}</Modal.Title>}
        </Modal.Header>
        <Modal.Body>

            {loading &&  <Section animation={HelperService.getRandomAnimation()}>
              <Loading className="m-2" />
            </Section>}

            {!loading && model && <>

              <Section animation={HelperService.getRandomAnimation()}>
                <div className="row mb-2">
                  <div className="col-sm-12">
                    {model?.type_sync_id!==BuildingStatTypes.capacity.val.toString() && <BuildingStatWidgetContent stat={model} containerClass={"bg-indigo text-white rounded-2 p-2"} />}

                    {model?.type_sync_id===BuildingStatTypes.capacity.val.toString() && model.dependencies && <>
                      <BuildingStatWidgetContent stat={model} containerClass={"bg-white border-indigo rounded-2 p-2"}
                                                 second={model.dependencies[0]}
                                                 third={model.dependencies[1]}
                                                 fourth={model.dependencies[2]}
                      />
                    </>}
                  </div>
                </div>
              </Section>

              {model?.type_sync_id===BuildingStatTypes.capacity.val.toString() && chartData?.length>1 && <Section className="mb-2 p-2">
                <MyHighchart titleY={model?.building_stat_type?.title} options={{
                  series: [
                    {
                      name: capacityKeys[0],
                      type: 'column',
                      color: "#737fed",
                      data: chartData.map((i)=> [i.parsedDate.valueOf(), i[capacityKeys[0]]])
                    },
                    {
                      name: capacityKeys[1],
                      type: 'column',
                      color: "#5ecb9b",
                      data: chartData.map((i)=> [i.parsedDate.valueOf(), i[capacityKeys[1]]])
                    },
                    {
                      name: capacityKeys[2],
                      type: 'column',
                      color: "#fa5c7c",
                      data: chartData.map((i)=> [i.parsedDate.valueOf(), i[capacityKeys[2]]])
                    },
                    {
                      name: capacityKeys[3],
                      type: 'column',
                      color: "#EEF2F7",
                      data: chartData.map((i)=> [i.parsedDate.valueOf(), i[capacityKeys[3]]])
                    },
                  ],
                  plotOptions: {
                    column: {
                      stacking: 'percent',
                      dataLabels: {
                        enabled: false
                      }
                    }
                  },
                  chart: {
                    height: 300
                  },
                  tooltip: {
                    formatter: function(){
                      const points = this.points;
                      const x = HelperService.getDayjs()(this.x);
                      let htmlPoints = points.reduce(function (s, point) {
                        return `${s}<br/><span style="display: inline-block; width: 12px; height: 12px; background: ${point.series.color}"></span> ${point.series.name}: <strong>${FormatService.formatNumber(point.y)}%</strong>`;
                      }, '<b>' + x.format('MM / YYYY') + '</b>');
                      return `<div style="
                        border-radius: 0.5rem 0.5rem 0.5rem 0;
                        background-color: white;
                        box-shadow: rgba(115,127,237,0.2) 0px 3px 10px 0px;
                        padding: 0.5rem;
                        line-height: 18px;
                      ">
                        ${htmlPoints}
                      </div>`;
                    },
                  }
                }} />
                {/*<ResponsiveBar*/}
                {/*  data={chartData}*/}
                {/*  keys={capacityKeys}*/}
                {/*  indexBy="period"*/}
                {/*  margin={{ top: 20, right: 50, bottom: 50, left: 50 }}*/}
                {/*  padding={0.3}*/}
                {/*  valueScale={{*/}
                {/*    type: "linear",*/}
                {/*    max: 120,*/}
                {/*    min: 0,*/}
                {/*  }}*/}
                {/*  indexScale={{ type: 'band', round: true }}*/}
                {/*  colors={["#737fed","#5ecb9b", "#fa5c7c", "#6c757d"]}*/}
                {/*  animate={true}*/}
                {/*  maxValue={130}*/}
                {/*  motionConfig={'molasses'}*/}
                {/*  enableLabel={true}*/}
                {/*  enableGridY={true}*/}
                {/*  axisTop={null}*/}
                {/*  axisRight={null}*/}
                {/*  axisBottom={{*/}
                {/*    tickSize: 5,*/}
                {/*    tickPadding: 5,*/}
                {/*    tickRotation: 0,*/}
                {/*    legend: 'Obdobie',*/}
                {/*    legendPosition: 'middle',*/}
                {/*    legendOffset: 32*/}
                {/*  }}*/}
                {/*  axisLeft={{*/}
                {/*    tickSize: 5,*/}
                {/*    tickPadding: 5,*/}
                {/*    tickRotation: 0,*/}
                {/*    legend: '%',*/}
                {/*    legendPosition: 'middle',*/}
                {/*    legendOffset: -40*/}
                {/*  }}*/}
                {/*  labelSkipWidth={12}*/}
                {/*  labelSkipHeight={12}*/}
                {/*  labelTextColor="#ffffff"*/}
                {/*/>*/}
              </Section>}

              {model?.type_sync_id!==BuildingStatTypes.capacity.val.toString() && chartData?.length>1 && <Section className="mb-2 p-2">
                <MyHighchart options={{
                  series: [
                      {
                        name: model?.building_stat_type?.title,
                        type: 'spline',
                        color: "#737fed",
                        data: chartData.map((i)=> [i.parsedDate.valueOf(), i.y])
                      }
                  ],
                  chart: {
                    height: 250
                  },
                }} />
              </Section>}

              <Section animation={HelperService.getRandomAnimation()}>
                <div className="row">
                  <div className="col-12">
                    {chartData?.length>0 && <>
                      <DataTableBase
                        progressPending={loading}
                        columns={columns}
                        data={chartData}
                        defaultSortFieldId={1}
                        defaultSortAsc={false}
                      />
                    </>}
                  </div>
                </div>
              </Section>

            </>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeModal}>Zatvoriť</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});

export default BuildingStatDetailModal;
